import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress, Alert, TextField, Select, MenuItem } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  fetchSkillSheetBank,
  createSkillSheetBankItem,
  updateSkillSheetBankItem,
  deleteSkillSheetBankItem,
} from "../../api/backend";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import { useConfigList } from "../../hooks/useConfigList";
import NoTenantSelected from "../../components/NoTenantSelected";

const D_STATIC = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)", surface2: "var(--color-surface-2)", surface3: "var(--color-surface-press-light)",
  danger: "#B42318", danger50: "rgba(180,35,24,.08)", danger100: "rgba(180,35,24,.18)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)",
};

function useD() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const accent = theme.palette.warning.main;
  return {
    ...D_STATIC,
    primary,
    primary700: theme.palette.primary.dark,
    primary10: alpha(primary, 0.06),
    primary50: alpha(primary, 0.10),
    green: secondary,
    green50: alpha(secondary, 0.10),
    green100: alpha(secondary, 0.20),
    amber: accent,
    amber50: alpha(accent, 0.10),
  };
}

function SvgIcon({ size = 15, sw = 2, children }) {
  return (
    <Box component="svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={sw}
      strokeLinecap="round" strokeLinejoin="round">{children}</Box>
  );
}

function Btn({ children, primary, danger, ghost, sm, onClick, disabled, icon }) {
  const D = useD();
  const h = sm ? 32 : 38;
  const px = sm ? "13px" : "18px";
  const fs = sm ? "12.5px" : "13px";
  const base = { height: h, px, borderRadius: sm ? "9px" : "11px", border: 0, fontFamily: "inherit", fontSize: fs, fontWeight: 700, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "7px", transition: "all .13s", whiteSpace: "nowrap" };
  const variant = primary ? { bgcolor: disabled ? D.surface3 : D.primary, color: disabled ? D.ink3 : "#fff", boxShadow: disabled ? "none" : `0 4px 12px -4px ${alpha(D.primary, 0.45)}`, "&:hover:not(:disabled)": { bgcolor: D.primary700 } }
    : danger ? { bgcolor: D.danger50, color: D.danger, border: `1.5px solid ${D.danger100}`, "&:hover": { bgcolor: D.danger100 } }
    : { bgcolor: D.surface2, color: D.ink2, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3 } };
  return <Box component="button" onClick={onClick} disabled={disabled} sx={{ ...base, ...variant }}>{icon}{children}</Box>;
}

function TagBadge({ tag, isSystem }) {
  const D = useD();
  return (
    <Box component="span" sx={{ fontSize: "11px", fontWeight: 700, px: "8px", py: "3px", borderRadius: "999px",
      bgcolor: isSystem ? D.amber50 : D.primary10, color: isSystem ? D.amber : D.primary }}>
      {tag}
    </Box>
  );
}

function SystemBadge() {
  const D = useD();
  return <Box component="span" sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", px: "8px", py: "2px", borderRadius: "999px", bgcolor: D.amber50, color: D.amber, border: `1px solid ${D.amber50}` }}>System</Box>;
}

const EMPTY_FORM = { title: "", description: "", items: "[]", passingScore: 80, timeLimitMin: "", scenarioContext: "", equipment: "", tags: "", category: "" };
const DEFAULT_CATEGORIES = ["Firefighter", "EMS", "Hazmat", "Search & Rescue", "Fire Officer", "General"];

function parseTags(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
}

function SheetForm({ initial, onSave, onCancel, saving }) {
  const D = useD();
  const CATEGORIES = useConfigList("SKILL_CATEGORY", DEFAULT_CATEGORIES);
  const controlSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: D.surface2, "& fieldset": { borderColor: D.line, borderWidth: "1.5px" }, "&:hover fieldset": { borderColor: D.ink3 }, "&.Mui-focused fieldset": { borderColor: D.primary, borderWidth: "1.5px" }, "&.Mui-focused": { bgcolor: "#fff" } },
    "& .MuiInputBase-input": { fontSize: "14px", fontWeight: 500, color: D.ink },
  };
  const multilineSx = { ...controlSx, "& .MuiOutlinedInput-root": { ...controlSx["& .MuiOutlinedInput-root"], height: "auto" } };
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const tagsDisplay = Array.isArray(form.tags) ? form.tags.join(", ") : form.tags;

  const handleSave = () => {
    const payload = { ...form,
      passingScore: parseInt(form.passingScore) || 80,
      timeLimitMin: form.timeLimitMin ? parseInt(form.timeLimitMin) : null,
      tags: JSON.stringify(parseTags(typeof form.tags === "string" ? form.tags : form.tags.join(", "))),
    };
    onSave(payload);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", p: "20px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
        <Box>
          <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Title *</Box>
          <TextField fullWidth size="small" value={form.title} onChange={set("title")} sx={{ ...controlSx, "& .MuiOutlinedInput-root": { ...controlSx["& .MuiOutlinedInput-root"], height: 44 } }} />
        </Box>
        <Box>
          <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Tags <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: D.ink4 }}>— comma separated</Box></Box>
          <TextField fullWidth size="small" placeholder="firefighter, nfpa, hose" value={tagsDisplay} onChange={set("tags")} sx={{ ...controlSx, "& .MuiOutlinedInput-root": { ...controlSx["& .MuiOutlinedInput-root"], height: 44 } }} />
        </Box>
      </Box>
      <Box>
        <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Description</Box>
        <TextField fullWidth multiline rows={2} value={form.description || ""} onChange={set("description")} sx={multilineSx} />
      </Box>
      <Box>
        <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Skill Items (JSON array)</Box>
        <TextField fullWidth multiline rows={4} placeholder='[{"name":"Skill 1","points":10},...]' value={form.items || "[]"} onChange={set("items")} sx={multilineSx} inputProps={{ style: { fontFamily: "monospace", fontSize: "13px" } }} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px" }}>
        <Box>
          <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Points Needed to Pass</Box>
          <TextField fullWidth size="small" type="number" value={form.passingScore} onChange={set("passingScore")} sx={{ ...controlSx, "& .MuiOutlinedInput-root": { ...controlSx["& .MuiOutlinedInput-root"], height: 44 } }} />
        </Box>
        <Box>
          <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Time Limit <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: D.ink4 }}>— min, optional</Box></Box>
          <TextField fullWidth size="small" type="number" value={form.timeLimitMin || ""} onChange={set("timeLimitMin")} sx={{ ...controlSx, "& .MuiOutlinedInput-root": { ...controlSx["& .MuiOutlinedInput-root"], height: 44 } }} />
        </Box>
        <Box>
          <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Category</Box>
          <Select fullWidth size="small" displayEmpty value={form.category || ""} onChange={set("category")} sx={{ ...controlSx, ...controlSx["& .MuiOutlinedInput-root"], height: 44 }}>
            <MenuItem value=""><em>None</em></MenuItem>
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
        <Box>
          <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Scenario Context <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: D.ink4 }}>— optional</Box></Box>
          <TextField fullWidth size="small" value={form.scenarioContext || ""} onChange={set("scenarioContext")} sx={{ ...controlSx, "& .MuiOutlinedInput-root": { ...controlSx["& .MuiOutlinedInput-root"], height: 44 } }} />
        </Box>
        <Box>
          <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>Equipment <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: D.ink4 }}>— optional</Box></Box>
          <TextField fullWidth size="small" value={form.equipment || ""} onChange={set("equipment")} sx={{ ...controlSx, "& .MuiOutlinedInput-root": { ...controlSx["& .MuiOutlinedInput-root"], height: 44 } }} />
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: "9px", justifyContent: "flex-end" }}>
        <Btn ghost sm onClick={onCancel}>Cancel</Btn>
        <Btn primary sm onClick={handleSave} disabled={!form.title || saving} icon={saving ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : null}>
          {saving ? "Saving…" : "Save to Bank"}
        </Btn>
      </Box>
    </Box>
  );
}

export default function SkillSheetBankPage() {
  const D = useD();
  const { tenantId: activeTenantId, isGlobalAdmin } = useActiveTenant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteErr, setDeleteErr] = useState(null);

  const load = useCallback(() => {
    if (isGlobalAdmin && !activeTenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchSkillSheetBank(search || undefined)
      .then(data => { setItems(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setErr("Failed to load skill sheet bank."); setLoading(false); });
  }, [search, activeTenantId, isGlobalAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    setSaving(true);
    try { const item = await createSkillSheetBankItem(form); setItems(prev => [item, ...prev]); setShowCreate(false); }
    catch { setErr("Failed to create."); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (id, form) => {
    setSaving(true);
    try { const updated = await updateSkillSheetBankItem(id, form); setItems(prev => prev.map(i => i.id === id ? updated : i)); setEditId(null); }
    catch { setErr("Failed to update."); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setDeleteErr(null);
    try { await deleteSkillSheetBankItem(id); setItems(prev => prev.filter(i => i.id !== id)); setDeleteId(null); }
    catch (e) { setDeleteErr(e?.response?.data?.message || "Cannot delete — may be in use by a course."); }
  };

  if (isGlobalAdmin && !activeTenantId) {
    return (
      <Box sx={{ maxWidth: 1160, mx: "auto" }}>
        <NoTenantSelected message="Select a training center from the top bar to view its skill sheet bank." />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1160, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "20px", flexWrap: "wrap", gap: "12px" }}>
        <Box>
          <Typography sx={{ fontSize: "26px", fontWeight: 800, letterSpacing: "-.03em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>Skill Sheet Bank</Typography>
          <Box sx={{ fontSize: "13.5px", color: D.ink3, mt: "4px" }}>Reusable skill checklists — assign to any course.</Box>
        </Box>
        <Btn primary onClick={() => { setShowCreate(true); setEditId(null); }}
          icon={<SvgIcon size={13} sw={2.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></SvgIcon>}>
          New Skill Sheet
        </Btn>
      </Box>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "12px" }}>{err}</Alert>}

      {/* Search */}
      <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "12px", px: "14px", display: "flex", alignItems: "center", gap: "10px", height: 44, mb: "16px", maxWidth: 380 }}>
        <SvgIcon size={14}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></SvgIcon>
        <Box component="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search skill sheets…"
          sx={{ flex: 1, border: 0, outline: 0, bgcolor: "transparent", fontSize: "14px", color: D.ink }} />
      </Box>

      {/* Create form */}
      {showCreate && (
        <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", boxShadow: D.shadowSm, overflow: "hidden", mb: "16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "11px", px: "20px", py: "13px", borderBottom: `1px solid ${D.line2}` }}>
            <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: D.primary10, color: D.primary, display: "grid", placeItems: "center" }}>
              <SvgIcon><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></SvgIcon>
            </Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 700, color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>New Skill Sheet</Typography>
          </Box>
          <SheetForm onSave={handleCreate} onCancel={() => setShowCreate(false)} saving={saving} />
        </Box>
      )}

      {/* Items */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: D.primary }} /></Box>
      ) : items.length === 0 ? (
        <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", p: "40px", textAlign: "center", color: D.ink4, fontSize: "13.5px" }}>
          {search ? "No skill sheets match your search." : "No skill sheets in the bank yet. Add one above."}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {items.map(item => (
            <Box key={item.id} sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", boxShadow: D.shadowSm, overflow: "hidden" }}>
              {editId === item.id ? (
                <>
                  <Box sx={{ px: "20px", py: "13px", borderBottom: `1px solid ${D.line2}`, display: "flex", alignItems: "center", gap: "8px" }}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>Edit: {item.title}</Typography>
                  </Box>
                  <SheetForm
                    initial={{ ...item, items: typeof item.items === "string" ? item.items : JSON.stringify(item.items ?? []), tags: parseTags(item.tags).join(", ") }}
                    onSave={form => handleUpdate(item.id, form)}
                    onCancel={() => setEditId(null)}
                    saving={saving}
                  />
                </>
              ) : (
                <Box sx={{ p: "16px 20px" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap", mb: "4px" }}>
                        <Box sx={{ fontSize: "15px", fontWeight: 700, color: D.ink }}>{item.title}</Box>
                        {item.isSystem && <SystemBadge />}
                      </Box>
                      {item.description && <Box sx={{ fontSize: "13px", color: D.ink3, mb: "8px" }}>{item.description}</Box>}
                      <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", mt: "6px" }}>
                        <Box sx={{ fontSize: "12px", color: D.ink4 }}>Passing: {item.passingScore} pts</Box>
                        {item.timeLimitMin && <Box sx={{ fontSize: "12px", color: D.ink4 }}>· {item.timeLimitMin} min</Box>}
                        {parseTags(item.tags).map(t => <TagBadge key={t} tag={t} isSystem={item.isSystem} />)}
                      </Box>
                    </Box>
                    {!item.isSystem && (
                      <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                        <Box component="button" onClick={() => setEditId(item.id)} sx={{ width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, "&:hover": { bgcolor: D.surface3, color: D.ink } }}>
                          <SvgIcon size={13}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></SvgIcon>
                        </Box>
                        <Box component="button" onClick={() => { setDeleteId(item.id); setDeleteErr(null); }} sx={{ width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, "&:hover": { bgcolor: D.danger50, color: D.danger, borderColor: D.danger100 } }}>
                          <SvgIcon size={13}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></SvgIcon>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {deleteId === item.id && (
                    <Box sx={{ mt: "12px", p: "12px 14px", bgcolor: D.danger50, border: `1.5px solid ${D.danger100}`, borderRadius: "10px" }}>
                      {deleteErr && <Box sx={{ fontSize: "12.5px", color: D.danger, mb: "8px" }}>{deleteErr}</Box>}
                      <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: D.danger, mb: "10px" }}>Delete "{item.title}" from the bank?</Box>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Btn ghost sm onClick={() => setDeleteId(null)}>Cancel</Btn>
                        <Btn danger sm onClick={() => handleDelete(item.id)}>Delete</Btn>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
